import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Clock,
    Users,
    Layers,
    Box,
    AlertCircle,
    Wind,
    PenTool,
    FileText,
    Wrench,
    RefreshCw,
    FileDown,
    Frame,
    Fan,
    Zap,
    Droplet,
    Flame,
    ClipboardCheck,
    FolderArchive,
    TrendingDown,
    Rocket,
    UsersRound,
    SlidersHorizontal,
    MapPin,
    PhoneCall,
    FileSearch2,
    FileSignature,
    PlayCircle,
    ClipboardList,
    HeartHandshake,
    Check,
    ArrowUpRight,
    Mail,
    Building2,
    HardHat,
    School,
    Factory,
    Warehouse,
    Hotel,
    Home as HomeIcon,
    Package,
    Landmark,
    ShieldAlert,
    Cpu,
    Server,
} from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { Button } from "../components/zen/Button";
import { AnimatedGrid } from "../components/zen/AnimatedGrid";
import { Accordion } from "../components/ui/Accordion";

const fade = {
    hidden: { opacity: 0, y: 16 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.75,
            delay: i * 0.08,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

const PROBLEM_CARDS = [
    {
        icon: Clock,
        title: "Hiring takes months.",
        description:
            "Bringing on skilled engineering talent is slow, expensive, and often unpredictable in today's market.",
    },
    {
        icon: Users,
        title: "Finding experienced Revit and BIM talent is difficult.",
        description:
            "Multidisciplinary Revit, MEP, and BIM coordination expertise is scarce and increasingly competitive to hire.",
    },
    {
        icon: Layers,
        title: "Project backlogs continue growing.",
        description:
            "As demand outpaces capacity, deadlines slip and margins compress.",
    },
];

const CAPABILITIES = [
    { icon: Box, title: "Revit Modeling", d: "Discipline-accurate 3D Revit models across architecture, structure, and MEP." },
    { icon: Layers, title: "BIM Coordination", d: "Cross-discipline model coordination and issue resolution." },
    { icon: AlertCircle, title: "Clash Detection", d: "Navisworks-based clash reports with resolution workflows." },
    { icon: Wind, title: "MEP Modeling", d: "Mechanical, electrical, and plumbing 3D modeling and layouts." },
    { icon: PenTool, title: "MEP Drafting", d: "Discipline drawings, single-line diagrams, and coordination sheets." },
    { icon: FileText, title: "Construction Documents", d: "Full CD sets ready for permitting and construction." },
    { icon: Wrench, title: "Shop Drawings", d: "Fabrication-ready shop drawings for MEP and structural systems." },
    { icon: RefreshCw, title: "CAD to Revit Conversion", d: "Migrate legacy CAD assets into structured BIM models." },
    { icon: FileDown, title: "PDF to CAD Conversion", d: "Convert scanned or PDF drawings into editable CAD files." },
    { icon: Frame, title: "Structural Drafting", d: "Foundations, framing, and detailing drawings." },
    { icon: Fan, title: "HVAC Drafting", d: "Duct layouts, equipment schedules, and mechanical drawings." },
    { icon: Zap, title: "Electrical Drafting", d: "Power, lighting, low-voltage, and single-line drawings." },
    { icon: Droplet, title: "Plumbing Drafting", d: "Water, waste, vent, and gas piping drawings." },
    { icon: Flame, title: "Fire Protection Drafting", d: "Sprinkler, standpipe, and life-safety drawings." },
    { icon: ClipboardCheck, title: "As-Built Drawings", d: "Field-verified as-built documentation." },
    { icon: FolderArchive, title: "Project Documentation", d: "Complete project records and coordination logs." },
];

const INDUSTRIES = [
    { label: "Commercial Buildings", icon: Building2 },
    { label: "Healthcare", icon: HeartHandshake },
    { label: "Education", icon: School },
    { label: "Industrial", icon: Factory },
    { label: "Warehouses", icon: Warehouse },
    { label: "Hospitality", icon: Hotel },
    { label: "Residential", icon: HomeIcon },
    { label: "Mixed Use", icon: Package },
    { label: "Government", icon: Landmark },
    { label: "Mission Critical", icon: ShieldAlert },
    { label: "Manufacturing", icon: Cpu },
    { label: "Data Centers", icon: Server },
];

const SOFTWARE = [
    "Revit",
    "AutoCAD",
    "Navisworks",
    "Civil 3D",
    "ETABS",
    "STAAD Pro",
    "HAP",
    "ETAP",
    "Bluebeam",
    "MS Project",
];

const WHY_CARDS = [
    { icon: TrendingDown, title: "Reduce production costs", d: "Deliver more work without adding permanent headcount or overhead." },
    { icon: Rocket, title: "Scale without hiring", d: "Add production capacity in days, not months." },
    { icon: UsersRound, title: "Dedicated engineering resources", d: "A committed team that learns your standards and workflows." },
    { icon: SlidersHorizontal, title: "Flexible project staffing", d: "Ramp up or down based on your project pipeline." },
    { icon: Zap, title: "Fast turnaround", d: "Meet aggressive deadlines with parallel production." },
    { icon: MapPin, title: "US-based communication", d: "One US-based point of contact for every deliverable." },
];

const PROCESS_STEPS = [
    { icon: PhoneCall, num: "01", title: "Schedule a Discovery Call", d: "A short conversation to understand your team's capacity needs." },
    { icon: FileSearch2, num: "02", title: "Share Your Drawings", d: "We review your drawings and project requirements under NDA." },
    { icon: FileSignature, num: "03", title: "Receive a Proposal", d: "A clear scope, timeline, and pricing structure." },
    { icon: PlayCircle, num: "04", title: "Dedicated Engineering Team Starts", d: "Onboarding, standards, and first deliverables." },
    { icon: ClipboardList, num: "05", title: "Review & Delivery", d: "US-managed QA and structured deliverable handoffs." },
    { icon: HeartHandshake, num: "06", title: "Ongoing Support", d: "Sustained engagement or project-based delivery, per your needs." },
];

const MANAGED_ITEMS = [
    "Communication management",
    "Coordination of deliverables",
    "Project quality oversight",
    "Bridge between U.S. clients and production team",
];

const FAQ_ITEMS = [
    {
        q: "Can you work with our standards?",
        a: "Yes. Our team adapts to your company standards — including templates, families libraries, title blocks, layer conventions, and naming standards. We treat your standards as ours for the duration of the engagement.",
    },
    {
        q: "Do you sign NDAs?",
        a: "Absolutely. We sign standard NDAs before any drawings, RFPs, or project materials are exchanged. Custom mutual NDAs are welcome, and we can typically execute them within one business day.",
    },
    {
        q: "Can you scale teams quickly?",
        a: "Yes. Our engineering production capacity is elastic. Depending on scope, we can ramp a dedicated team from a single resource to a full multidisciplinary group within one to two weeks.",
    },
    {
        q: "Do you support Revit?",
        a: "Revit is a core competency across architecture, structural, and MEP disciplines. We also support AutoCAD, Navisworks, Civil 3D, ETABS, STAAD Pro, HAP, ETAP, Bluebeam, and MS Project.",
    },
    {
        q: "How does communication work?",
        a: "You always work with your U.S.-based ZenMe project lead. We handle scoping, coordination, QA, and delivery timelines. Direct communication with production staff can be enabled on request, but the default is a single accountable point of contact.",
    },
    {
        q: "Can we start with one project?",
        a: "Yes. Many partnerships begin with a single project or pilot engagement. From there, teams often expand into ongoing production support as trust and standards alignment build.",
    },
];

const ASTRA_URL = "https://astraec.com/bim-service/";

export default function Bim() {
    return (
        <PageShell>
            <div data-testid="page-bim">
                {/* HERO */}
                <section
                    className="relative isolate overflow-hidden pt-[88px]"
                    data-testid="bim-hero"
                >
                    <AnimatedGrid />
                    <Container className="relative z-10">
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                            className="max-w-3xl py-24 lg:py-32"
                        >
                            <motion.div variants={fade} custom={0}>
                                <Eyebrow withDot>BIM & Engineering Services</Eyebrow>
                            </motion.div>
                            <motion.div variants={fade} custom={1}>
                                <Heading as="h1" fluidSize="h1" className="mt-6">
                                    Expand Your Engineering Team Without Hiring
                                </Heading>
                            </motion.div>
                            <motion.p
                                variants={fade}
                                custom={2}
                                className="mt-7 max-w-2xl text-[17px] leading-[1.65] text-text-secondary"
                            >
                                ZenMe helps U.S. engineering firms, contractors
                                and architects increase production capacity
                                through a dedicated multidisciplinary BIM and
                                engineering delivery team.
                            </motion.p>
                            <motion.div
                                variants={fade}
                                custom={3}
                                className="mt-10 flex flex-wrap items-center gap-3"
                            >
                                <Button
                                    to="/bim/inquiry"
                                    variant="primary"
                                    size="lg"
                                    data-testid="bim-hero-cta-schedule"
                                >
                                    Schedule a Call
                                </Button>
                                <a
                                    href={ASTRA_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="bim-hero-cta-partner"
                                    className="inline-flex items-center gap-2 rounded-full border border-border-default px-7 py-3 text-[14.5px] font-medium text-text-primary transition-all duration-300 ease-luxury hover:text-[var(--accent)] hover:border-[rgba(124,255,79,0.5)] hover:bg-[rgba(124,255,79,0.04)]"
                                >
                                    View Our Engineering Partner
                                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                                </a>
                            </motion.div>
                            <motion.p
                                variants={fade}
                                custom={4}
                                className="mt-8 max-w-xl text-[14px] text-text-muted"
                            >
                                Serving engineering firms, contractors,
                                architects and design-build companies
                                throughout the United States.
                            </motion.p>
                        </motion.div>
                    </Container>
                </section>

                {/* SECTION 1 — THE PROBLEM */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>The Problem</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Production capacity is the bottleneck.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                            {PROBLEM_CARDS.map((c, i) => (
                                <motion.div
                                    key={c.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.7,
                                        delay: i * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="zen-card rounded-2xl border border-border-subtle bg-elevated p-7"
                                    data-testid={`bim-problem-${i}`}
                                >
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--bg-elevated-2)] border border-border-subtle">
                                        <c.icon className="h-5 w-5 text-[var(--accent)]" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="mt-6 font-display text-[20px] font-semibold leading-tight text-text-primary">
                                        {c.title}
                                    </h3>
                                    <p className="mt-3 text-[14.5px] leading-[1.65] text-text-secondary">
                                        {c.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                        <p className="mx-auto mt-16 max-w-2xl text-center text-[17px] leading-[1.65] text-text-secondary">
                            Instead of hiring another full-time employee,
                            expand your production capacity on demand.
                        </p>
                    </Container>
                </Section>

                {/* SECTION 2 — CAPABILITIES */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Capabilities</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Full-service engineering production.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {CAPABILITIES.map((c, i) => (
                                <div
                                    key={c.title}
                                    className="zen-card rounded-xl border border-border-subtle bg-elevated p-5"
                                    data-testid={`bim-capability-${i}`}
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] border border-border-subtle">
                                        <c.icon className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.5} />
                                    </div>
                                    <h4 className="mt-5 font-display text-[16px] font-medium leading-snug text-text-primary">
                                        {c.title}
                                    </h4>
                                    <p className="mt-2 text-[13px] leading-[1.55] text-text-secondary">
                                        {c.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* SECTION 3 — INDUSTRIES */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Industries</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Sectors we deliver for.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                            {INDUSTRIES.map((it, i) => (
                                <div
                                    key={it.label}
                                    className="zen-card flex items-center gap-3 rounded-xl border border-border-subtle bg-elevated p-4"
                                    data-testid={`bim-industry-${i}`}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] border border-border-subtle">
                                        <it.icon className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={1.5} />
                                    </span>
                                    <span className="text-[13.5px] font-medium text-text-primary">
                                        {it.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* SECTION 4 — SOFTWARE */}
                <Section className="border-y border-border-subtle bg-elevated">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Software</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                The tools we use.
                            </Heading>
                        </div>
                        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                            {SOFTWARE.map((name, i) => (
                                <div
                                    key={name}
                                    className="flex items-center justify-center rounded-xl border border-border-subtle bg-[var(--bg-elevated-2)] px-4 py-6 text-center"
                                    data-testid={`bim-software-${i}`}
                                >
                                    <span className="font-display text-[15px] font-medium text-text-primary">
                                        {name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* SECTION 5 — WHY ZENME */}
                <Section className="border-b border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Why ZenMe</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                The engineering partnership advantage.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {WHY_CARDS.map((w, i) => (
                                <div
                                    key={w.title}
                                    className="zen-card rounded-2xl border border-border-subtle bg-elevated p-7"
                                    data-testid={`bim-why-${i}`}
                                >
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--bg-elevated-2)] border border-border-subtle">
                                        <w.icon className="h-5 w-5 text-[var(--accent)]" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="mt-6 font-display text-[20px] font-semibold leading-tight text-text-primary">
                                        {w.title}
                                    </h3>
                                    <p className="mt-3 text-[14.5px] leading-[1.65] text-text-secondary">
                                        {w.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* SECTION 6 — PROCESS TIMELINE (6 steps) */}
                <Section className="border-b border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Process</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                From first call to ongoing delivery.
                            </Heading>
                        </div>
                        <div
                            className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6"
                            data-testid="bim-process"
                        >
                            {PROCESS_STEPS.map((s, i) => (
                                <div
                                    key={s.num}
                                    className="relative rounded-2xl border border-border-subtle bg-elevated p-6"
                                    data-testid={`bim-process-${i}`}
                                >
                                    <span className="font-display text-[12px] tracking-widest-2 uppercase text-text-muted">
                                        {s.num}
                                    </span>
                                    <div className="mt-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] border border-border-subtle">
                                        <s.icon className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.5} />
                                    </div>
                                    <h4 className="mt-5 font-display text-[16px] font-semibold leading-snug text-text-primary">
                                        {s.title}
                                    </h4>
                                    <p className="mt-2 text-[13px] leading-[1.55] text-text-secondary">
                                        {s.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* SECTION 7 — POSITIONING: MANAGED DELIVERY */}
                <Section className="border-b border-border-subtle">
                    <Container>
                        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                            <div className="lg:col-span-5">
                                <Eyebrow>Why ZenMe</Eyebrow>
                                <Heading as="h2" className="mt-4">
                                    Managed delivery, not outsourcing.
                                </Heading>
                                <ul className="mt-10 space-y-4" data-testid="bim-managed-list">
                                    {MANAGED_ITEMS.map((m) => (
                                        <li key={m} className="flex items-start gap-3">
                                            <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(124,255,79,0.4)] bg-[rgba(124,255,79,0.06)]">
                                                <Check
                                                    className="h-3 w-3 text-[var(--accent)]"
                                                    strokeWidth={2.25}
                                                />
                                            </span>
                                            <span className="text-[15px] text-text-primary">
                                                {m}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="lg:col-span-7 space-y-6 text-[17px] leading-[1.7] text-text-secondary">
                                <p>
                                    ZenMe is your U.S.-based engineering
                                    partner. We manage communication,
                                    coordinate deliverables, and oversee
                                    project quality — bridging the gap between
                                    U.S. clients and our engineering production
                                    team.
                                </p>
                                <p>
                                    You always communicate with ZenMe. We
                                    handle scoping, project management,
                                    standards alignment, quality reviews, and
                                    delivery timelines. Our engineering
                                    production is powered by a trusted partner,
                                    but every touchpoint — proposal, milestone,
                                    revision, and final handoff — flows through
                                    your ZenMe project lead.
                                </p>
                                <p>
                                    This gives you the scale and cost advantage
                                    of a global engineering team with the
                                    accountability, communication clarity, and
                                    business relationship of a U.S.-based
                                    partnership.
                                </p>
                            </div>
                        </div>
                    </Container>
                </Section>

                {/* SECTION 8 — PARTNER CARD */}
                <Section className="border-b border-border-subtle">
                    <Container>
                        <div
                            className="mx-auto max-w-3xl rounded-2xl border border-border-subtle bg-elevated p-10 sm:p-14 relative overflow-hidden"
                            data-testid="bim-partner-card"
                            style={{
                                boxShadow:
                                    "0 24px 48px -24px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.01)",
                            }}
                        >
                            <div
                                aria-hidden
                                className="absolute -top-16 -right-16 h-[280px] w-[280px] rounded-full"
                                style={{
                                    background:
                                        "radial-gradient(ellipse at center, rgba(124,255,79,0.10) 0%, transparent 60%)",
                                    filter: "blur(60px)",
                                }}
                            />
                            <div className="relative">
                                <span className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-[rgba(255,255,255,0.02)] px-3 py-1">
                                    <span
                                        aria-hidden
                                        className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]"
                                    />
                                    <span className="text-[11px] uppercase tracking-widest-2 text-text-secondary">
                                        Partner
                                    </span>
                                </span>
                                <Heading as="h2" className="mt-6" fluidSize="h3">
                                    Astra Engineering Consultants
                                </Heading>
                                <p className="mt-6 text-[17px] leading-[1.7] text-text-secondary">
                                    ZenMe partners with Astra Engineering
                                    Consultants, a multidisciplinary
                                    engineering consultancy with teams across
                                    India and the Middle East. Engineering
                                    production is performed through Astra while
                                    ZenMe manages U.S. client communication,
                                    coordination, delivery oversight, and
                                    project success.
                                </p>
                                <div className="mt-8">
                                    <a
                                        href={ASTRA_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="bim-partner-cta"
                                        className="zen-glow-on-hover inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-7 py-3 text-[14.5px] font-medium text-[#0a0a0b] transition-all duration-300 ease-luxury hover:brightness-110"
                                    >
                                        Visit Astra Engineering
                                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Container>
                </Section>

                {/* SECTION 9 — FAQ */}
                <Section className="border-b border-border-subtle">
                    <Container>
                        <div className="mx-auto max-w-3xl">
                            <div className="text-center">
                                <Eyebrow>FAQ</Eyebrow>
                                <Heading as="h2" className="mt-4">
                                    Frequently asked questions.
                                </Heading>
                            </div>
                            <div className="mt-14">
                                <Accordion
                                    items={FAQ_ITEMS}
                                    testIdPrefix="bim-faq"
                                />
                            </div>
                        </div>
                    </Container>
                </Section>

                {/* SECTION 10 — FINAL CTA */}
                <section className="relative isolate overflow-hidden">
                    <div className="absolute inset-0 -z-10">
                        <div
                            className="absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                            style={{
                                background:
                                    "radial-gradient(ellipse at center, rgba(124,255,79,0.10) 0%, rgba(124,255,79,0.03) 35%, transparent 65%)",
                                filter: "blur(60px)",
                            }}
                        />
                        <div className="zen-grid opacity-50" />
                    </div>
                    <Container>
                        <div
                            className="mx-auto max-w-3xl py-32 text-center lg:py-40"
                            data-testid="bim-final-cta"
                        >
                            <Eyebrow withDot>Let's Talk</Eyebrow>
                            <Heading as="h2" className="mt-6">
                                Need More Production Capacity?
                            </Heading>
                            <p className="mx-auto mt-6 max-w-xl text-[17px] leading-[1.65] text-text-secondary">
                                Whether you need help with one project or
                                ongoing engineering support, ZenMe can help
                                your team deliver more work without increasing
                                permanent headcount.
                            </p>
                            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                                <Button
                                    to="/bim/inquiry"
                                    variant="primary"
                                    size="lg"
                                    data-testid="bim-final-cta-schedule"
                                >
                                    Schedule a Call
                                </Button>
                                <a
                                    href="mailto:contact@zenmegroup.com"
                                    data-testid="bim-final-cta-email"
                                    className="inline-flex items-center gap-2 rounded-full border border-border-default px-7 py-3 text-[14.5px] font-medium text-text-primary transition-all duration-300 ease-luxury hover:text-[var(--accent)] hover:border-[rgba(124,255,79,0.5)] hover:bg-[rgba(124,255,79,0.04)]"
                                >
                                    <Mail className="h-4 w-4" strokeWidth={1.5} />
                                    Email Us
                                </a>
                            </div>
                        </div>
                    </Container>
                </section>
            </div>
        </PageShell>
    );
}
